package uz.ferro.erp.pages.login

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import mui.material.Box
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.FormControlVariant
import mui.material.Paper
import mui.material.TextField
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.events.ChangeEvent
import react.dom.onChange
import react.router.dom.useSearchParams
import react.useContext
import react.useState
import uz.ferro.erp.Colors
import uz.ferro.erp.LocaleContext
import uz.ferro.erp.api.jsonSerializer
import uz.ferro.erp.error.HttpException
import uz.ferro.erp.feature.user.model.Role
import uz.ferro.erp.locale.LocaleDict
import uz.ferro.erp.manager.AuthManager
import uz.ferro.erp.navigateToLink
import uz.ferro.erp.ui.AppText
import uz.ferro.erp.ui.AppTextBold
import uz.ferro.erp.ui.VerticalLayout
import uz.ferro.erp.util.localized
import uz.ferro.erp.util.styleInMobile
import web.cssom.AlignItems
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.JustifyContent
import web.cssom.Position
import web.cssom.integer
import web.cssom.pct
import web.cssom.pt
import web.cssom.px
import web.html.HTMLInputElement
import web.html.InputType

val LoginPage = FC<Props> {

    var loginInput by useState("")
    var passwordInput by useState("")
    val locale = useContext(LocaleContext)
    val queryParams = useSearchParams()
    val redirect = queryParams.component1()["redirect"]
    var errorMessage by useState("")

    fun handleError(e: Exception) {
        if (e is HttpException) {
            try {
                val el = jsonSerializer.parseToJsonElement(e.errorResponse)
                val message = el.jsonObject["errorMessage"]!!.jsonPrimitive.content
                if (message.isNotBlank()) {
                    errorMessage = message
                }
            } catch (e: Exception) {
                // no-op
            }
        }
    }

    fun login() {
        MainScope().launch {
            try {
                val result = AuthManager.authenticate(
                    login = loginInput,
                    password = passwordInput
                )

                if (result.user != null) {
                    when (result.user.role?.name) {
                        Role.ADMIN,
                        Role.SUPER_ADMIN -> navigateToLink("/admin")

                        else -> navigateToLink("/")
                    }
                }

                errorMessage = ""
            } catch (e: Exception) {
                e.printStackTrace()
                handleError(e)
            }
        }
    }

    Box {
        sx {
            position = Position.absolute
            top = 0.px
            right = 0.px
            bottom = 0.px
            left = 0.px
            backgroundColor = Colors.loginBgColor
            display = Display.flex
            flexDirection = FlexDirection.column
            justifyContent = JustifyContent.center
            alignItems = styleInMobile(AlignItems.center, AlignItems.center)
            padding = 16.px
        }

        VerticalLayout {
            space = 20.px
            sx {
                alignItems = AlignItems.stretch
                width = styleInMobile(100.pct, 500.px)
            }
            AppTextBold {
                size = 36
                color = Colors.textPrimary
                text = LocaleDict.appName.localized()
            }

            Paper {
                sx {
                    width = 100.pct
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    alignItems = AlignItems.stretch
                    padding = styleInMobile(16.px, 24.px)
                }

                Box {
                    sx {
                        width = 100.pct
                        fontSize = 16.pt
                        color = Colors.title
                        fontWeight = integer(500)
                    }
                    +locale.enterToSystem.localized()
                }

                TextField {
                    sx {
                        width = 100.pct
                        marginTop = 24.px
                    }

                    label = ReactNode(locale.login.localized())
                    autoComplete = "off"
                    variant = FormControlVariant.outlined
                    value = loginInput
                    onChange = { event ->
                        event as ChangeEvent<HTMLInputElement>
                        loginInput = event.target.value.trim()
                    }
                }

                TextField {
                    sx {
                        width = 100.pct
                        marginTop = 16.px
                    }
                    label = ReactNode(locale.password.localized())
                    variant = FormControlVariant.outlined
                    value = passwordInput
                    type = InputType.password
                    onChange = { event ->
                        event as ChangeEvent<HTMLInputElement>
                        passwordInput = event.target.value.trim()
                    }
                    onKeyDown = { event ->
                        if (event.key == "Enter") {
                            if (loginInput.isNotBlank() && passwordInput.isNotBlank()) {
                                login()
                            }
                            event.preventDefault();
                        }
                    }
                }

                Box {
                    sx {
                        marginTop = 16.px
                        display = Display.flex
                        flexDirection = FlexDirection.row
                        justifyContent = JustifyContent.right
                    }

                    Button {
                        sx {

                        }
                        variant = ButtonVariant.contained
                        disabled = loginInput.isBlank() || passwordInput.isBlank()
                        onClick = {
                            login()
                        }

                        +locale.signIn.localized()
                    }
                }

                if (errorMessage.isNotBlank()) {
                    AppText {
                        sx {
                            marginTop = 16.px
                        }
                        color = Colors.errorText
                        text = errorMessage
                    }
                }
            }
        }


    }
}